import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Allergy,
  AssistanceNeed,
  EkycStatus,
  PatientDetail,
  PatientIdentity,
  Payer,
} from '@/features/patients/types';
import type { TicketStatus, Vitals } from '@/features/queue/types';
import { api } from '@/lib/api';

export type DocumentKind =
  | 'medical_certificate'
  | 'time_slip'
  | 'referral_letter'
  | 'imaging_referral'
  | 'vaccination_certificate'
  | 'outside_prescription';

export const DOCUMENT_LABELS: Record<DocumentKind, string> = {
  medical_certificate: 'Medical certificate',
  time_slip: 'Time slip',
  referral_letter: 'Referral letter',
  imaging_referral: 'X-ray or scan referral',
  vaccination_certificate: 'Vaccination certificate',
  outside_prescription: 'Prescription for a pharmacy',
};

export interface ClinicalDocument {
  id: string;
  number: string;
  kind: DocumentKind;
  data: Record<string, unknown>;
  encounterId: string;
  issuedAt: string;
  issuedBy: { id: string; fullName: string } | null;
  verificationCode: string;
  revokedAt: string | null;
  /** Imaging referrals: tracking by the lab desk (RL-724). */
  imaging?: {
    status: 'referred' | 'appointment_made' | 'report_in' | 'reviewed';
    appointmentAt: string | null;
    reportAt: string | null;
    reportText: string | null;
    reviewedAt: string | null;
    reviewComment: string | null;
  };
}

export type RecordKind =
  | 'in_clinic_test'
  | 'vaccination'
  | 'phq9'
  | 'adverse_reaction'
  | 'child_protection'
  | 'domestic_violence'
  | 'emergency_step';

export interface EncounterRecord {
  id: string;
  kind: RecordKind;
  data: Record<string, unknown>;
  restricted: boolean;
  recordedAt: string;
  recordedBy: { id: string; fullName: string };
}

export interface Procedure {
  id: string;
  code: string;
  name: string;
  consumables: string | null;
  consent: 'not_needed' | 'needed' | 'signed';
  consentAt: string | null;
  addedAt: string;
}

export interface Diagnosis {
  id: string;
  code: string;
  description: string;
  isPrimary: boolean;
  addedAt: string;
}

export type ConsultationMissingCode =
  'diagnosis' | 'consent' | 'required_vitals' | 'valid_mmc_apc' | 'rx_warnings';

/** A server-compatible reason that prevents a consultation being signed/closed. */
export interface ConsultationMissingItem {
  code: ConsultationMissingCode;
  label: string;
  detail?: string;
}

/** A consent form signed in the patient app (API RL-908). */
export interface AppConsentForm {
  id: string;
  number: string;
  status: 'signed' | 'witnessed' | 'rejected' | 'revoked' | string;
  procedureCode: string;
  title: string;
  templateKey: string;
  templateRevision: number;
  signerName: string;
  nameMatchesRecord: boolean;
  signedAt: string;
  witnessedAt: string | null;
  rejectedAt: string | null;
  revokedAt: string | null;
}

/** POST procedure-consents/:id/reject `reason` (shown to the patient). */
export const CONSENT_REJECT_REASONS = {
  wrong_procedure: 'Wrong procedure',
  details_changed: 'Details changed',
  signature_unclear: 'Signature unclear',
  not_signed_by_patient: 'Not signed by the patient',
  other: 'Other',
} as const;
export type ConsentRejectReason = keyof typeof CONSENT_REJECT_REASONS;

/** RL-1854: the visit's doctor witnesses or rejects an app-signed form. */
export function useAppConsentActions(encounterId: string) {
  const qc = useQueryClient();
  const onSuccess = () =>
    void qc.invalidateQueries({ queryKey: ['encounter', encounterId] });
  return {
    witness: useMutation({
      mutationFn: (id: string) => api.post(`/procedure-consents/${id}/witness`),
      onSuccess,
    }),
    reject: useMutation({
      mutationFn: (v: {
        id: string;
        reason: ConsentRejectReason;
        note: string | null;
      }) =>
        api.post(`/procedure-consents/${v.id}/reject`, {
          reason: v.reason,
          note: v.note,
        }),
      onSuccess,
    }),
  };
}

/** RL-1696: the follow-up booked at close, or why none was booked. */
export interface EncounterFollowUp {
  /** Absent on servers before RL-1696 (then a follow-up was always booked). */
  status?: 'booked' | 'not_booked';
  appointmentId: string | null;
  startsAt: string | null;
  doctor?: { id: string; fullName: string } | null;
  /** false = booked with another doctor of the branch. */
  sameDoctor?: boolean | null;
  targetDate?: string | null;
  reason?: 'no_free_slot' | 'no_service' | null;
  /** RL-1696 review: true = the patient's app asks them to book it (Home card). */
  patientPrompted?: boolean;
}

export interface Encounter {
  id: string;
  status: 'open' | 'closed';
  notes: string;
  /** RL-1694/1695: what the patient said in the app; null = walk-in or none. */
  patientReason?: string | null;
  version: number;
  startedAt: string;
  closedAt: string | null;
  closedBy: { id: string; fullName: string } | null;
  doctor: {
    id: string;
    fullName: string;
    mmcNumber?: string | null;
    apcValidUntil?: string | null;
  };
  branch: { id: string; code: string; name: string };
  mcDays: number;
  followUpMonths: number | null;
  /** RL-1956: shown to the patient in the app; null = none. */
  patientAdvice?: string | null;
  /** RL-1696/1697: what happened to the follow-up at close; null = open or none asked. */
  followUp: EncounterFollowUp | null;
  notifiable: { code: string; disease: string }[];
  aiConsent: { granted: boolean; recordedAt: string | null };
  payer: Payer | null;
  cover: { status: 'covered' | 'not_covered'; checkedAt: string } | null;
  vitalsHistory: Vitals[];
  procedures: Procedure[];
  /** RL-908: consent forms the patient signed in the app for this visit. */
  procedureConsents?: AppConsentForm[];
  records: EncounterRecord[];
  restrictedHidden: number;
  documents: ClinicalDocument[];
  attachments: {
    id: string;
    fileName: string;
    contentType: string;
    sizeBytes: number;
    addedAt: string;
    addedBy: { id: string; fullName: string };
  }[];
  patient: {
    id: string;
    fullName: string;
    dateOfBirth: string | null;
    sex: 'male' | 'female' | null;
    nationality: string;
    assistanceNeeds: AssistanceNeed[];
    ekycStatus: EkycStatus;
    ekycVerifiedAt: string | null;
    safeContactOnly: boolean;
    allergies: Allergy[];
    identity: PatientIdentity | null;
  };
  queueTicket: {
    id: string;
    number: number;
    status: TicketStatus;
    room: string | null;
    service: { name: string } | null;
    arrivedAt: string;
  } | null;
  vitals: Vitals | null;
  diagnoses: Diagnosis[];
  addenda: {
    id: string;
    body: string;
    author: { id: string; fullName: string };
    createdAt: string;
  }[];
  /** Present in v16/v17 responses; omitted by older API versions. */
  signing?: { missing: ConsultationMissingItem[] };
}

export function useEncounter(id: string | undefined) {
  return useQuery({
    queryKey: ['encounter', id],
    queryFn: async ({ signal }) =>
      (await api.get<Encounter>(`/encounters/${id}`, { signal })).data,
    enabled: !!id,
  });
}

export function useOpenEncounter() {
  return useMutation({
    mutationFn: async (queueTicketId: string) =>
      (await api.post<Encounter>('/encounters', { queueTicketId })).data,
  });
}

export function useEncounterActions(id: string) {
  const qc = useQueryClient();
  const onSuccess = (e: Encounter) => {
    qc.setQueryData(['encounter', id], e);
    void qc.invalidateQueries({ queryKey: ['queue'] });
  };
  return {
    saveNotes: useMutation({
      mutationFn: async (input: { version: number; notes: string }) =>
        (await api.patch<Encounter>(`/encounters/${id}/notes`, input)).data,
      onSuccess,
    }),
    addDiagnosis: useMutation({
      mutationFn: async (input: {
        code: string;
        description: string;
        isPrimary?: boolean;
      }) =>
        (await api.post<Encounter>(`/encounters/${id}/diagnoses`, input)).data,
      onSuccess,
    }),
    removeDiagnosis: useMutation({
      mutationFn: async (diagnosisId: string) =>
        (
          await api.post<Encounter>(
            `/encounters/${id}/diagnoses/${diagnosisId}/remove`,
          )
        ).data,
      onSuccess,
    }),
    close: useMutation({
      mutationFn: async (input: {
        version: number;
        patientAdvice?: string | null;
      }) => (await api.post<Encounter>(`/encounters/${id}/close`, input)).data,
      onSuccess,
    }),
    addAddendum: useMutation({
      mutationFn: async (body: string) =>
        (await api.post<Encounter>(`/encounters/${id}/addenda`, { body })).data,
      onSuccess,
    }),
    setPlan: useMutation({
      mutationFn: async (input: {
        mcDays?: number;
        followUpMonths?: number | null;
        patientAdvice?: string | null;
      }) => (await api.patch<Encounter>(`/encounters/${id}/plan`, input)).data,
      onSuccess,
    }),
    aiConsent: useMutation({
      mutationFn: async (granted: boolean) =>
        (await api.post<Encounter>(`/encounters/${id}/ai-consent`, { granted }))
          .data,
      onSuccess,
    }),
    addProcedure: useMutation({
      mutationFn: async (code: string) =>
        (await api.post<Encounter>(`/encounters/${id}/procedures`, { code }))
          .data,
      onSuccess,
    }),
    consentProcedure: useMutation({
      mutationFn: async (procedureId: string) =>
        (
          await api.post<Encounter>(
            `/encounters/${id}/procedures/${procedureId}/consent`,
          )
        ).data,
      onSuccess,
    }),
    removeProcedure: useMutation({
      mutationFn: async (procedureId: string) =>
        (
          await api.post<Encounter>(
            `/encounters/${id}/procedures/${procedureId}/remove`,
          )
        ).data,
      onSuccess,
    }),
    /** One of the "More for this visit" forms or documents (path under the encounter). */
    submit: useMutation({
      mutationFn: async (v: { path: string; body?: object }) =>
        (await api.post<Encounter>(`/encounters/${id}/${v.path}`, v.body ?? {}))
          .data,
      onSuccess: (e: Encounter) => {
        onSuccess(e);
        void qc.invalidateQueries({ queryKey: ['patient'] });
        void qc.invalidateQueries({ queryKey: ['prescriptions', id] });
      },
    }),
    attach: useMutation({
      mutationFn: async (file: File) =>
        (
          await api.post<Encounter>(
            `/encounters/${id}/attachments`,
            undefined,
            {
              query: { fileName: file.name },
              rawBody: file,
            },
          )
        ).data,
      onSuccess,
    }),
  };
}

/** Opens an attachment in a new tab (download is audited by the API). */
export async function openAttachment(attachmentId: string) {
  const blob = (
    await api.get<Blob>(`/attachments/${attachmentId}`, {
      responseType: 'blob',
    })
  ).data;
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export interface ConsultationOptions {
  procedures: {
    code: string;
    name: string;
    consumables: string | null;
    consentRequired: boolean;
  }[];
  inClinicTests: string[];
  vaccines: string[];
  /** API RL-1852: the clinic's childhood schedule (absent on older APIs). */
  childVaccines?: string[];
  orders: string[];
  referralSpecialties: string[];
  imagingExams: string[];
  emergencySteps: string[];
}

export function useConsultationOptions() {
  return useQuery({
    queryKey: ['consultation-options'],
    queryFn: async ({ signal }) =>
      (await api.get<ConsultationOptions>('/consultation/options', { signal }))
        .data,
    staleTime: Infinity,
  });
}

export interface FormularyItem {
  code: string;
  name: string;
  generic: string;
  strengthMg: number | null;
  unit: string;
  doses: string[];
  groups: string[];
  maxDailyMg: number | null;
  controlled: boolean;
  highAlert: boolean;
}

export function useFormulary(enabled = true) {
  return useQuery({
    queryKey: ['formulary'],
    queryFn: async ({ signal }) =>
      (
        await api.get<{
          medicines: FormularyItem[];
          frequencies: string[];
          allergyGroups: { key: string; label: string }[];
        }>('/formulary', { signal })
      ).data,
    staleTime: Infinity,
    enabled,
  });
}

export interface DoctorToday {
  clinicDate: string;
  myPatients: { total: number; inRoom: number; waiting: number; seen: number };
  resultsToReview: { total: number; criticalHeld: number };
  teleconsults: null;
  inbox: null;
  upNext: {
    ticketId: string;
    number: number;
    patient: { id: string; fullName: string };
    service: { id: string; name: string } | null;
    payer: { id: string; name: string } | null;
    arrivedAt: string;
    vitalsTaken: boolean;
    seeFirst: boolean;
    encounterId: string | null;
  }[];
  needsDecision: {
    labOrderId: string;
    patient: { id: string; fullName: string };
    test: string;
    value: string | null;
    unit: string | null;
    referenceRange: string | null;
    flag: 'normal' | 'low' | 'high' | 'abnormal' | 'critical' | null;
  }[];
  yourDay: {
    at: string;
    kind: 'visit' | 'booking';
    state: 'seen' | 'now' | 'waiting' | 'booked';
    patient: { id: string; fullName: string };
    detail: string;
    ticketId: string | null;
  }[];
}

export function useDoctorToday(enabled = true) {
  return useQuery({
    queryKey: ['doctor-today'],
    queryFn: async ({ signal }) =>
      (await api.get<DoctorToday>('/doctor/today', { signal })).data,
    refetchInterval: 15_000,
    enabled,
  });
}

export interface PatientRecord {
  patient: PatientDetail;
  clinical: boolean;
  visits: {
    ticketId: string | null;
    date: string;
    arrivedAt: string;
    number: number | null;
    status: TicketStatus;
    branch: { id: string; name: string };
    service: { id: string; name: string } | null;
    payer: { id: string; name: string } | null;
    doctor: { id: string; fullName: string } | null;
    encounter: {
      id: string;
      status: 'open' | 'closed';
      startedAt: string;
      closedAt: string | null;
      doctor: { id: string; fullName: string };
      diagnoses: { code: string; description: string; isPrimary: boolean }[];
      /** RL-2185: signed prescriptions of that visit (absent on an older API). */
      medicines?: { medicine: string; dose: string; frequency: string }[];
      addenda: number;
    } | null;
  }[];
  results: {
    labOrderId: string;
    number: string | null;
    encounterId: string;
    status: 'results_ready' | 'reviewed';
    resultsReadyAt: string | null;
    reviewedAt: string | null;
    reviewedBy: { id: string; fullName: string } | null;
    reviewComment: string | null;
    items: {
      id?: string;
      testName: string;
      resultValue: string | null;
      resultUnit: string | null;
      referenceRange: string | null;
      resultFlag: string | null;
      /** Same per-test state as the lab inbox and the doctor's review (RL-724). */
      review?: 'released' | 'held' | 'to_review';
      doctorComment?: string | null;
    }[];
  }[];
  outsideReports?: {
    id: string;
    source: string | null;
    reportDate: string | null;
    results: {
      test: string;
      value: string;
      unit: string | null;
      referenceRange: string | null;
      flag: string | null;
    }[];
    enteredAt: string;
    enteredBy: { id: string; fullName: string };
  }[];
  documents: ClinicalDocument[];
  attachments: {
    id: string;
    encounterId: string;
    fileName: string;
    contentType: string;
    sizeBytes: number;
    addedAt: string;
  }[];
  vitals: Vitals[];
}

export function usePatientRecord(id: string | undefined) {
  return useQuery({
    queryKey: ['patient-record', id],
    queryFn: async ({ signal }) =>
      (await api.get<PatientRecord>(`/patients/${id}/record`, { signal })).data,
    enabled: !!id,
  });
}
